import { Box, Checkbox, Divider, Paper, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Locale, ReferenceDisplayValue, TableDescriptor } from '@finance/contracts';
import { resolveTKey } from '../../../i18n/resolve-key';
import type { EvaluationContext } from '../../../runtime/conditions/evaluate-condition.ts';
import type { ResolvedAction } from '../../../runtime/actions/action-resolution.ts';
import { getVisibleOrderedColumns } from './table-state.ts';
import type { TableState } from './table-state.ts';
import { resolveCellRenderer } from './renderers/cell-renderer-registry.ts';
import { TableRowActions } from './TableRowActions.tsx';
import type { TableActionContext } from './table-renderer-props.ts';

interface TableResponsiveCardsProps {
  descriptor: TableDescriptor;
  data: Record<string, unknown>[];
  state: TableState;
  evalCtx: EvaluationContext;
  locale: Locale;
  mode: 'cards' | 'collapse';
  getRowId: (row: Record<string, unknown>) => string | undefined;
  referenceDisplayMap?: Record<string, Record<string, ReferenceDisplayValue>> | null;
  onAction?: (action: ResolvedAction, context: TableActionContext) => void | Promise<void>;
  onRowActivate?: (row: Record<string, unknown>, resolvedAction?: ResolvedAction) => void;
  selectionEnabled?: boolean;
  onToggleRowSelection?: (rowId: string) => void;
}

export function TableResponsiveCards({
  descriptor,
  data,
  state,
  evalCtx,
  locale,
  mode,
  getRowId,
  referenceDisplayMap,
  onAction,
  onRowActivate,
  selectionEnabled = false,
  onToggleRowSelection,
}: TableResponsiveCardsProps) {
  const { t } = useTranslation();
  const visibleColumns = useMemo(() => {
    const columns = getVisibleOrderedColumns(descriptor, state);
    const priorities = descriptor.responsive?.priorityColumns ?? [];
    const rank = new Map(priorities.map((field, index) => [field, index]));
    const ordered = [...columns].sort((left, right) => {
      const leftRank = rank.get(left.field) ?? Number.MAX_SAFE_INTEGER;
      const rightRank = rank.get(right.field) ?? Number.MAX_SAFE_INTEGER;
      return leftRank - rightRank;
    });
    if (mode !== 'collapse' || priorities.length === 0) return ordered;
    const prioritySet = new Set(priorities);
    return ordered.filter(column => prioritySet.has(column.field));
  }, [descriptor, mode, state]);

  return (
    <Stack spacing={1.25} sx={{ p: 1.25 }} data-testid="table-responsive-cards">
      {data.map(row => {
        const rowId = getRowId(row);
        const selected = rowId ? state.selection.selectedIds.includes(rowId) : false;
        return (
          <Paper
            key={rowId ?? JSON.stringify(row)}
            variant="outlined"
            component="article"
            tabIndex={onRowActivate ? 0 : undefined}
            onClick={onRowActivate ? () => onRowActivate(row) : undefined}
            onKeyDown={
              onRowActivate
                ? event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onRowActivate(row);
                    }
                  }
                : undefined
            }
            sx={{
              p: 1.5,
              minWidth: 0,
              cursor: onRowActivate ? 'pointer' : 'default',
              '&:focus-visible': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: 2,
              },
            }}
          >
            <Stack spacing={1.25}>
              {(selectionEnabled || (descriptor.rowActions?.length ?? 0) > 0) && (
                <Box
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
                  onClick={event => event.stopPropagation()}
                >
                  {selectionEnabled && rowId ? (
                    <Checkbox
                      size="small"
                      checked={selected}
                      onChange={() => onToggleRowSelection?.(rowId)}
                      slotProps={{ input: { 'aria-label': t(resolveTKey('common.select')) } }}
                    />
                  ) : (
                    <Box />
                  )}
                  {(descriptor.rowActions?.length ?? 0) > 0 && (
                    <TableRowActions
                      actions={descriptor.rowActions ?? []}
                      row={row}
                      evalCtx={evalCtx}
                      onAction={onAction}
                    />
                  )}
                </Box>
              )}

              {visibleColumns.map((column, index) => {
                const rendererDescriptor = resolveCellRenderer(column);
                const Renderer = rendererDescriptor.component;
                return (
                  <Box key={column.id} sx={{ minWidth: 0 }}>
                    {index > 0 && <Divider sx={{ mb: 1.25 }} />}
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mb: 0.25, fontWeight: 600, overflowWrap: 'anywhere' }}
                    >
                      {t(resolveTKey(column.labelKey))}
                    </Typography>
                    <Box
                      sx={{
                        minWidth: 0,
                        fontSize: '0.875rem',
                        fontWeight: index === 0 ? 600 : 400,
                        textAlign: column.align ?? 'left',
                        overflowWrap: column.wrapping === 'wrap' ? 'anywhere' : 'normal',
                        overflow: 'hidden',
                        textOverflow: column.truncation === 'none' ? 'clip' : 'ellipsis',
                      }}
                    >
                      <Renderer
                        value={row[column.field]}
                        column={column}
                        row={row}
                        locale={locale}
                        referenceDisplayMap={referenceDisplayMap}
                      />
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
}
